import { db } from './firebaseConfig';
import {
  collection,
  query,
  getDocs,
  doc,
  updateDoc,
  Timestamp,
  writeBatch,
  where,
  orderBy
} from 'firebase/firestore';
import { parseDate } from './pdfUtils';

/**
 * Utility function to normalize a date to midnight in local timezone
 * This matches the logic in CreateDOR component
 */
const normalizeDateToLocal = (date) => {
  if (!date) return null;
  
  let dateObj;
  if (date instanceof Timestamp) {
    dateObj = date.toDate();
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    dateObj = parseDate(date);
  }
  
  if (!dateObj || isNaN(dateObj.getTime())) return null;
  
  // Create a date at midnight in local timezone
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth();
  const day = dateObj.getDate();
  
  return new Date(year, month, day, 0, 0, 0, 0);
};

/**
 * Check for duplicate DORs for a specific trainee
 */
export const findDuplicateDORs = async (traineeId = null) => {
  try {
    
    // Build query based on whether traineeId is provided
    let dorsQuery;
    if (traineeId) {
      dorsQuery = query(
        collection(db, 'dors'),
        where('traineeId', '==', traineeId),
        orderBy('date', 'asc')
      );
    } else {
      dorsQuery = query(
        collection(db, 'dors'),
        orderBy('date', 'asc')
      );
    }
    
    const snapshot = await getDocs(dorsQuery);
    const dorsByTraineeAndDate = {};
    const duplicates = [];
    
    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      const traineeId = data.traineeId;
      const date = data.date;
      
      if (!traineeId || !date) return;
      
      // Normalize the date to check for duplicates
      const normalizedDate = normalizeDateToLocal(date);
      if (!normalizedDate) return;
      
      const dateKey = normalizedDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      const key = `${traineeId}_${dateKey}`;
      
      if (!dorsByTraineeAndDate[key]) {
        dorsByTraineeAndDate[key] = [];
      }
      
      dorsByTraineeAndDate[key].push({
        id: doc.id,
        reportId: data.reportId,
        traineeId: traineeId,
        traineeName: data.traineeName || 'Unknown',
        date: date,
        dateOnly: data.dateOnly,
        normalizedDate: dateKey,
        createdAt: data.createdAt,
        createdBy: data.createdBy,
        trainerName: data.trainerName || 'Unknown'
      });
    });
    
    // Find duplicates
    Object.entries(dorsByTraineeAndDate).forEach(([key, dors]) => {
      if (dors.length > 1) {
        duplicates.push({
          key,
          traineeId: dors[0].traineeId,
          traineeName: dors[0].traineeName,
          date: dors[0].normalizedDate,
          count: dors.length,
          dors: dors.sort((a, b) => {
            // Sort by creation date to identify which was created first
            const aCreated = a.createdAt?.toDate?.() || new Date(0);
            const bCreated = b.createdAt?.toDate?.() || new Date(0);
            return aCreated - bCreated;
          })
        });
      }
    });
    
    return duplicates;
  } catch (error) {
    console.error('Error finding duplicate DORs:', error);
    throw error;
  }
};

/**
 * Migrate DORs to add the dateOnly field
 */
export const migrateDORsWithDateOnly = async () => {
  try {
    
    // Get all DORs
    const dorsQuery = query(collection(db, 'dors'));
    const snapshot = await getDocs(dorsQuery);
    
    
    const batch = writeBatch(db);
    let updateCount = 0;
    let errorCount = 0;
    const errors = [];
    
    snapshot.docs.forEach((docSnapshot) => {
      try {
        const data = docSnapshot.data();
        const dorId = docSnapshot.id;
        
        // Check if dateOnly field already exists
        if (data.dateOnly) {
          return;
        }
        
        // Get the date field
        const date = data.date;
        if (!date) {
          console.warn(`DOR ${data.reportId || dorId} has no date field`);
          errorCount++;
          errors.push({
            id: dorId,
            reportId: data.reportId,
            error: 'No date field'
          });
          return;
        }
        
        // Normalize the date
        const normalizedDate = normalizeDateToLocal(date);
        if (!normalizedDate) {
          console.warn(`DOR ${data.reportId || dorId} has invalid date`);
          errorCount++;
          errors.push({
            id: dorId,
            reportId: data.reportId,
            error: 'Invalid date',
            date: date
          });
          return;
        }
        
        // Create the dateOnly timestamp
        const dateOnlyTimestamp = Timestamp.fromDate(normalizedDate);
        
        // Update the document
        const dorRef = doc(db, 'dors', dorId);
        batch.update(dorRef, {
          dateOnly: dateOnlyTimestamp
        });
        
        updateCount++;
      } catch (error) {
        console.error(`Error processing DOR ${docSnapshot.id}:`, error);
        errorCount++;
        errors.push({
          id: docSnapshot.id,
          error: error.message
        });
      }
    });
    
    if (updateCount > 0) {
      await batch.commit();
    } else {
    }    
    if (errors.length > 0) {
      console.error('Errors encountered:', errors);
    }
    
    return {
      success: true,
      updated: updateCount,
      errors: errorCount,
      errorDetails: errors
    };
  } catch (error) {
    console.error('Migration failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Remove duplicate DORs, keeping the oldest one
 */
export const removeDuplicateDORs = async (duplicateSet) => {
  try {
    if (!duplicateSet || !duplicateSet.dors || duplicateSet.dors.length < 2) {
      throw new Error('Invalid duplicate set provided');
    }
    
    // Sort by creation date - keep the oldest
    const sortedDORs = [...duplicateSet.dors].sort((a, b) => {
      const aCreated = a.createdAt?.toDate?.() || new Date(0);
      const bCreated = b.createdAt?.toDate?.() || new Date(0);
      return aCreated - bCreated;
    });
    
    const toKeep = sortedDORs[0];
    const toDelete = sortedDORs.slice(1);
    

    
    const batch = writeBatch(db);
    
    toDelete.forEach((dor) => {
      const dorRef = doc(db, 'dors', dor.id);
      batch.delete(dorRef);
    });
    
    await batch.commit();
    
    return {
      success: true,
      kept: toKeep,
      deleted: toDelete
    };
  } catch (error) {
    console.error('Error removing duplicates:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Generate a report of all duplicate DORs
 */
export const generateDuplicateReport = async () => {
  try {
    const duplicates = await findDuplicateDORs();
    
    if (duplicates.length === 0) {
      return {
        success: true,
        message: 'No duplicate DORs found!',
        duplicates: []
      };
    }
    
    const report = duplicates.map(dup => ({
      trainee: `${dup.traineeName} (ID: ${dup.traineeId})`,
      date: dup.date,
      count: dup.count,
      dors: dup.dors.map(dor => ({
        reportId: dor.reportId,
        trainer: dor.trainerName,
        createdAt: dor.createdAt?.toDate?.().toLocaleString() || 'Unknown',
        hasDateOnly: !!dor.dateOnly
      }))
    }));
    
    return {
      success: true,
      totalDuplicateSets: duplicates.length,
      totalDuplicateDORs: duplicates.reduce((sum, dup) => sum + dup.count - 1, 0),
      report
    };
  } catch (error) {
    console.error('Error generating duplicate report:', error);
    return {
      success: false,
      error: error.message
    };
  }
};